import { Select } from "@kobalte/core/select"
import { TextField } from "@kobalte/core/text-field"
import { ToggleGroup } from "@kobalte/core/toggle-group"
import type { AccentHue } from "@repo/fika-design-system/color"
import type { IconName } from "@repo/fika-design-system/icon"
import {
  createMemo,
  createSignal,
  For,
  type JSXElement,
  type ParentProps,
  Show,
  splitProps,
} from "solid-js"
import { Icon } from "@/designSystem/components/icon"
import { Box } from "../box"
import { Tooltip } from "../tooltip"
import {
  buttonRecipe,
  buttonToggleClass,
  buttonToggleCountClass,
  buttonToggleGroupClass,
  dividerClass,
  fileInputClass,
  horizontalDividerClass,
  iconClass,
  menuClass,
  sectionClass,
  selectClass,
  selectContentClass,
  selectIconClass,
  selectItemCountClass,
  selectItemLabelClass,
  selectItemRecipe,
  selectListboxClass,
  selectTriggerRecipe,
  selectValueClass,
  selectValueTextClass,
  textFieldClass,
} from "./mini.css"

type MiniTextFieldProps = {
  defaultValue?: string
  disabled?: boolean
  label?: string
  onChange?: (value: string) => void
  placeholder?: string
  type?: "text" | "number" | "password" | "email" | "search"
  fieldSizing?: "content"
  autofocus?: boolean
  value?: string
  onBlur?: (event: FocusEvent) => void
  onKeyDown?: (event: KeyboardEvent) => void
}
function MiniTextField(props: MiniTextFieldProps) {
  return (
    <TextField
      value={props.value}
      onChange={props.onChange}
      defaultValue={props.defaultValue}
      disabled={props.disabled}
    >
      <TextField.Input
        class={textFieldClass({ fieldSizing: props.fieldSizing })}
        type={props.type}
        placeholder={props.placeholder}
        autofocus={props.autofocus}
        disabled={props.disabled}
        onBlur={props.onBlur}
        onKeyDown={props.onKeyDown}
      />
    </TextField>
  )
}

type MiniSelectItem = {
  value: string | null
  label: string
  iconName?: IconName
  hue?: AccentHue
  count?: number
}

type MiniSelectProps = {
  items: MiniSelectItem[]
  value?: string
  defaultValue?: string
  onChange: (value: string | null) => void
  hideValue?: boolean
  hideChevron?: boolean
  fixedIcon?: IconName
  defaultLabel?: string
  fixedLabel?: string
  portal?: boolean
}

function MiniSelect(props: MiniSelectProps) {
  const options = createMemo(() => props.items.map(item => item.value))

  function getItem(value: string) {
    return props.items.find(item => item.value === value)!
  }

  return (
    <Select
      options={options()}
      value={props.value}
      class={selectClass}
      onChange={props.onChange}
      defaultValue={props.defaultValue}
      itemComponent={iProps => {
        const item = getItem(iProps.item.rawValue)
        return (
          <Select.Item
            item={iProps.item}
            class={selectItemRecipe({ hue: item.hue })}
          >
            <Show when={item.iconName}>
              {iconName => <Icon name={iconName()} size="18" />}
            </Show>
            <Select.ItemLabel class={selectItemLabelClass}>
              {item.label ?? iProps.item.rawValue}
            </Select.ItemLabel>
            <Show when={item.count}>
              <span class={selectItemCountClass({ hue: item.hue })}>
                {item.count}
              </span>
            </Show>
          </Select.Item>
        )
      }}
    >
      <Select.Trigger
        type="button"
        class={selectTriggerRecipe({
          hue: (props.value ? getItem(props.value)?.hue : null) ?? "sand",
        })}
      >
        <Select.Value<string> class={selectValueClass}>
          {state => {
            const item = state.selectedOption()
              ? getItem(state.selectedOption())
              : null
            const iconName = props.fixedIcon ?? item?.iconName
            return (
              <>
                <Show when={iconName}>
                  {icon => <Icon name={icon()} size="18" />}
                </Show>
                <Show when={props.fixedLabel || !props.hideValue}>
                  <span class={selectValueTextClass}>
                    {props.fixedLabel
                      ? props.fixedLabel
                      : props.defaultLabel && !state.selectedOption()
                        ? props.defaultLabel
                        : (item?.label ?? state.selectedOption())}
                  </span>
                </Show>
              </>
            )
          }}
        </Select.Value>
        <Show when={!props.hideChevron}>
          <Select.Icon class={selectIconClass}>
            <Icon name="chevron-down" size="18" />
          </Select.Icon>
        </Show>
      </Select.Trigger>
      <Show
        when={props.portal !== false}
        fallback={
          <Select.Content class={selectContentClass}>
            <Select.Listbox class={selectListboxClass} />
          </Select.Content>
        }
      >
        <Select.Portal>
          <Select.Content class={selectContentClass}>
            <Select.Listbox class={selectListboxClass} />
          </Select.Content>
        </Select.Portal>
      </Show>
    </Select>
  )
}

type MiniToggleGroupValue = {
  value: string
  count?: number
  iconName?: IconName
  labelIconName?: IconName
  title?: string
  shortcut?: string
}
type MiniToggleGroupValuesProps = {
  values: MiniToggleGroupValue[]
}
function MiniToggleGroupValues(props: MiniToggleGroupValuesProps) {
  return (
    <For each={props.values}>
      {value => (
        <Tooltip
          description={value.title ?? value.value}
          shortcut={value.shortcut}
        >
          <ToggleGroup.Item
            class={buttonToggleClass}
            aria-label={value.title ?? value.value}
            value={value.value}
          >
            <Show
              when={value.iconName}
              fallback={
                <Box flexDirection="row" alignItems="center" gap="4">
                  <Show when={value.labelIconName}>
                    {iconName => <Icon name={iconName()} size="14" />}
                  </Show>
                  <span>{value.title ?? value.value}</span>
                  <Show when={value.count}>
                    <span class={buttonToggleCountClass}>{value.count}</span>
                  </Show>
                </Box>
              }
            >
              {iconName => (
                <Icon title={value.title} name={iconName()} size="18" />
              )}
            </Show>
          </ToggleGroup.Item>
        </Tooltip>
      )}
    </For>
  )
}

type MiniToggleGroupProps = {
  value?: string | null
  defaultValue?: string
  onChange: (value: string | null) => void
} & MiniToggleGroupValuesProps
function MiniToggleGroup(props: MiniToggleGroupProps) {
  return (
    <ToggleGroup
      class={buttonToggleGroupClass}
      value={props.value}
      defaultValue={props.defaultValue}
      onChange={props.onChange}
    >
      <MiniToggleGroupValues values={props.values} />
    </ToggleGroup>
  )
}

type MiniToggleGroupMultipleProps = {
  value?: string[]
  defaultValue?: string[]
  onChange: (value: string[]) => void
} & MiniToggleGroupValuesProps
function MiniToggleGroupMultiple(props: MiniToggleGroupMultipleProps) {
  return (
    <ToggleGroup
      class={buttonToggleGroupClass}
      value={props.value}
      defaultValue={props.defaultValue}
      onChange={props.onChange}
      multiple
    >
      <MiniToggleGroupValues values={props.values} />
    </ToggleGroup>
  )
}

type MiniImagePickerProps = {
  onChange: (file: File) => void
  title?: string
  iconName?: IconName
}

function MiniImagePicker(props: MiniImagePickerProps) {
  async function handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (file) {
      props.onChange(file)
    }
  }

  return (
    <label
      class={buttonToggleClass}
      aria-label={props.title ?? "insert image"}
      title={props.title ?? "insert image"}
    >
      <input
        class={fileInputClass}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
      />
      <Icon
        title={props.title ?? "insert image"}
        name={props.iconName ?? "image-up"}
        size="18"
        aria-hidden="true"
      />
    </label>
  )
}

type ButtonProps = {
  iconName: IconName
  onClick: (e: MouseEvent) => void
  hue?: AccentHue
  title?: JSXElement
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  disabled?: boolean
  active?: boolean
} & ParentProps

function Button(props: ButtonProps) {
  const [isPromisePending, setIsPromisePending] = createSignal(false)
  const [local, rest] = splitProps(props, [
    "onClick",
    "iconName",
    "title",
    "hue",
    "active",
  ])

  const isActive = createMemo(() => !!props.active || isPromisePending())
  const disabled = createMemo(() => !!props.disabled || isActive())

  const handleClick = (event: MouseEvent) => {
    if (!local.onClick || typeof local.onClick !== "function") return

    const result = local.onClick(event) as unknown

    if (result instanceof Promise) {
      setIsPromisePending(true)
      result.finally(() => setIsPromisePending(false))
    }

    return result
  }

  return (
    <button
      {...rest}
      onClick={handleClick}
      type="button"
      class={buttonRecipe({ hue: props.hue })}
      disabled={disabled()}
      aria-busy={isActive()}
    >
      <Icon name={props.iconName} size="18" />
      {props.title}
    </button>
  )
}

function StaticIcon(props: { name: IconName; disabled?: boolean }) {
  return (
    <div class={iconClass}>
      <Icon
        name={props.name}
        size="18"
        color={props.disabled ? "sand400" : "sand800"}
      />
    </div>
  )
}

function Menu(props: ParentProps & { class?: string; ghost?: boolean }) {
  return (
    <div
      class={[menuClass({ ghost: props.ghost ?? false }), props.class]
        .filter(Boolean)
        .join(" ")}
    >
      {props.children}
    </div>
  )
}

function Section(props: ParentProps) {
  return <div class={sectionClass}>{props.children}</div>
}

function Divider() {
  return <hr class={dividerClass} />
}

function HorizontalDivider() {
  return <hr class={horizontalDividerClass} />
}

function Mini() {}

Mini.TextField = MiniTextField
Mini.Select = MiniSelect
Mini.ToggleGroup = MiniToggleGroup
Mini.ToggleGroupMultiple = MiniToggleGroupMultiple
Mini.ImagePicker = MiniImagePicker
Mini.Button = Button
Mini.Icon = StaticIcon
Mini.Menu = Menu
Mini.Section = Section
Mini.Divider = Divider
Mini.HorizontalDivider = HorizontalDivider

export { Mini }
